/**
 * P40 (Feb 28, 2026) — Eligibility 270/271 batches admin page.
 *
 * AZAI super-admin views the daily 270 batch history, recent
 * eligibility changes (deltas), and can trigger a run-now.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowsClockwise, Lightning, CheckCircle, XCircle, Clock, Warning, ArrowsCounterClockwise,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { formatDateTime as fmtDate } from '../lib/utils';

const StatusPill = ({ status }) => {
  const map = {
    'Submitted': { cls: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock, label: 'Awaiting 271' },
    'Response Received': { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle, label: 'Response received' },
    'Failed': { cls: 'bg-rose-100 text-rose-800 border-rose-200', icon: XCircle, label: 'Failed' },
    'NoPatients': { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Warning, label: 'No patients' },
  };
  const m = map[status] || { cls: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock, label: status };
  const Icon = m.icon;
  return (
    <Badge className={`${m.cls} gap-1`}>
      <Icon size={11} weight="fill" /> {m.label}
    </Badge>
  );
};

const EligibilityBatchesPage = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [bRes, cRes] = await Promise.all([
        api.get('/eligibility-batch?limit=25'),
        api.get('/eligibility-batch/changes/recent?days=7'),
      ]);
      setBatches(bRes.data.batches || []);
      setChanges(cRes.data.changes || []);
    } catch (e) {
      toast.error('Failed to load batches', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/eligibility-batch/run-now');
      const d = r.data;
      if (d._skipped) {
        toast.info('Batch already ran in the last 6h', {
          description: `Existing batch ${d.id?.slice(0, 8)} — ${d.patient_count} patients`,
        });
      } else if (d.status === 'NoPatients') {
        toast.warning('No patients to query', {
          description: 'No active patients or pending claims found.',
        });
      } else {
        toast.success('270 batch sent to ODM', {
          description: `${d.patient_count} patients across ${Object.keys(d.agency_breakdown || {}).length} agencies. 271 response in ~4-24h.`,
        });
      }
      refresh();
    } catch (e) {
      toast.error('Run failed', { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="eligibility-batches-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Eligibility 270/271
          </h1>
          <p className="text-slate-500 mt-1">
            Daily batch eligibility check via ODM SFTP. Runs nightly at 2 AM ET; only changes are reported.
          </p>
        </div>
        <Button
          onClick={runNow}
          disabled={running}
          className="bg-indigo-900 hover:bg-indigo-800 text-white"
          data-testid="run-now-btn"
        >
          {running
            ? <><ArrowsClockwise size={16} className="mr-2 animate-spin" />Submitting…</>
            : <><Lightning size={16} className="mr-2" weight="fill" />Run batch now</>}
        </Button>
      </div>

      {/* Recent changes panel — only shows if there's anything to show */}
      {changes.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="recent-changes-panel">
          <h2 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <ArrowsCounterClockwise size={20} weight="fill" />
            {changes.length} eligibility change{changes.length !== 1 ? 's' : ''} in the last 7 days
          </h2>
          <div className="max-h-64 overflow-y-auto space-y-1.5">
            {changes.slice(0, 25).map((c) => (
              <div key={c.id} className="text-sm flex items-center gap-3 py-1 border-b border-amber-200/50 last:border-0">
                <span className="text-slate-500 text-xs w-32 shrink-0">{fmtDate(c.created_at)}</span>
                <span className="font-medium text-slate-900 flex-1 truncate">{c.patient_name}</span>
                <span className="text-slate-500 text-xs">
                  {c.previous_status || '—'} → <span className="font-semibold text-amber-900">{c.new_status}</span>
                </span>
                {c.new_mco_payer_id && c.new_mco_payer_id !== c.previous_mco_payer_id && (
                  <Badge variant="outline" className="text-xs">MCO: {c.new_mco_payer_id}</Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batches table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-lg font-semibold text-slate-900">Recent batches</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : batches.length === 0 ? (
          <div className="p-10 text-center">
            <div className="text-slate-700 font-medium mb-1">No batches yet</div>
            <div className="text-sm text-slate-500">Click "Run batch now" to send the first 270 to ODM.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3">Filename</th>
                  <th className="px-5 py-3 text-right">Patients</th>
                  <th className="px-5 py-3 text-right">Agencies</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Changes</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr
                    key={b.id}
                    className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => navigate(`/admin/eligibility-batches/${b.id}`)}
                    data-testid={`batch-row-${b.id}`}
                  >
                    <td className="px-5 py-3 text-slate-700 text-xs">{fmtDate(b.created_at)}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-700 truncate max-w-xs">{b.filename}</td>
                    <td className="px-5 py-3 text-right font-semibold">{b.patient_count}</td>
                    <td className="px-5 py-3 text-right">{Object.keys(b.agency_breakdown || {}).length}</td>
                    <td className="px-5 py-3"><StatusPill status={b.status} /></td>
                    <td className="px-5 py-3 text-right">
                      {b.changes_count > 0 ? (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">{b.changes_count}</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-300">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EligibilityBatchesPage;
